import ContentShell from "components/ContentShell";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import TranslateIcon from "@mui/icons-material/Translate";
import configData from "config/config.json";
import { useSnackbar } from "hooks/useSnackbar";
import useStyles from "styles/banner/bannerEditor";
import { SimpleTextInput } from "components/FormTextInput";
import {
  createBanner,
  getBannerInfo,
  updateBanner,
} from "services/homeBannerService";
import FilePicker from "components/FilePicker";
import * as Yup from "yup";
import {
  uploadImageV2,
  uploadContentTranslations,
} from "services/uploadService";
import { SimpleSelectInput } from "components/FormSelectInput";
import SearchableInput from "components/SearchableInput";
import BulkTranslationUpload from "components/BulkTranslationUpload";
import TranslationButtons from "components/TranslationButtons";
import useOpenClose from "hooks/useOpenClose";
import ViewLanguageModal from "screens/modals/ViewLanguageModal";
import SimpleSwitch from "components/Switch";
import ColorPicker from "components/ColorPicker";
import sessionHelperInstance from "services/sessionHelper";
import { ImageType, DEFAULT_IMAGE_DIMENSIONS } from "utils/imageConstants";
import ScheduleFields from "components/schedule/ScheduleFields";
import SectionCard from "components/SectionCard";
import BannerPreview from "./BannerPreview";
import {
  assertScheduleForSubmit,
  getScheduleLockState,
} from "utils/contentSchedule";
import { listPackLibraryImages } from "services/packServices";

interface BannerDetails {
  title: string;
  titleTrans: Record<string, any>;
  navigationPath: string;
  body: string;
  bodyTrans: Record<string, any>;
  fileType: string;
  isActive: boolean;
  order: any;
  textColor: string;
  isTitleBold: boolean;
  isBodyBold: boolean;
  territory: string;
  operators: any[];
  imageUrl?: string;
  archivedAt?: string;
  startDate?: string | null;
  endDate?: string | null;
  imageRepositoryId?: string | null;
}

const _bannerDetails: BannerDetails = {
  title: "",
  titleTrans: {},
  navigationPath: "STORE",
  body: "",
  bodyTrans: {},
  fileType: "",
  isActive: false,
  order: null,
  textColor: "#FFFFFF",
  isTitleBold: false,
  isBodyBold: false,
  territory: "all",
  operators: [],
  startDate: null,
  endDate: null,
  imageRepositoryId: null,
};

const bannerValidation = Yup.object().shape({
  title: Yup.string().required("Title is required"),
  navigationPath: Yup.string().required("Navigation Path is required"),
  body: Yup.string().required("Body is required"),
  textColor: Yup.string().required("Color is required"),
  order: Yup.number().positive().min(0).required("Order is required"),
});

const HomeBannerEditor = () => {
  const styles = useStyles();
  const location = useLocation();
  const { showSnackbar } = useSnackbar();
  const id = location.pathname.split("/").slice(-1)[0];
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [bannerDetails, setBannerDetails] = useState(_bannerDetails);
  const [image, setImage] = useState(null);
  const [imageDimensions, setImageDimensions] = useState<{
    targetWidth: number;
    targetHeight: number;
    originalWidth: number;
    originalHeight: number;
  } | null>(null);
  const [translationFile, setTranslationFile] = useState<File | null>(null);
  const { isOpen, close, open } = useOpenClose();
  const [modalData, setModalData] = useState({
    language: "",
    inputValue: "",
    field: null,
    viewData: [],
  });
  const [libraryImages, setLibraryImages] = useState<any[]>([]);
  const initialSchedule = useRef<{
    startDate?: string | null;
    endDate?: string | null;
  }>({});
  const { isStatusScheduleControlled, isScheduleBlockedByStatus } =
    getScheduleLockState({
      isLive: bannerDetails.isActive,
      startDate: bannerDetails.startDate,
      endDate: bannerDetails.endDate,
    });
  const selectedLibraryImage = libraryImages.find(
    (img) => img.id === bannerDetails.imageRepositoryId,
  );
  const libraryPreviewUrl = selectedLibraryImage?.imageUrl;
  const uploadPreviewUrl = useMemo(
    () => (image ? URL.createObjectURL(image) : ""),
    [image],
  );
  const previewImageUrl =
    uploadPreviewUrl || libraryPreviewUrl || bannerDetails?.imageUrl;

  const cancel = () => navigate(configData.routes.homeBanners.homeBanners);

  const submit = async () => {
    try {
      setLoading(true);
      let response;
      await bannerValidation.validate(bannerDetails);
      assertScheduleForSubmit(bannerDetails, initialSchedule.current);

      if (id === "create") {
        response = await createBanner({
          ...bannerDetails,
          order: Number(bannerDetails?.order),
        });

        if (response?.data?.fileUploadUrl) {
          const fileUploadUrl = response?.data?.fileUploadUrl;
          await upload(fileUploadUrl);
        }

        if (translationFile && response?.data?._id) {
          await uploadContentTranslations(
            translationFile,
            "BANNER",
            response.data._id,
          );
        }
      } else {
        const { fileType, order, imageUrl, archivedAt, isActive, ...rest } =
          bannerDetails;
        if (fileType !== "") rest["fileType"] = fileType;
        // As in PackEditor: switching the status off only unlocks the dates. While the schedule owns
        // the status the stored one is kept, so an edit never pauses a live banner or blocks its start.
        if (!isStatusScheduleControlled) rest["isActive"] = isActive;

        response = await updateBanner({ ...rest, order: Number(order) }, id);

        if (bannerDetails.fileType !== "" && response?.data?.fileUploadUrl) {
          const fileUploadUrl = response?.data?.fileUploadUrl;
          await upload(fileUploadUrl);
        }
      }
      showSnackbar(response.message, "success");
      setLoading(false);
      navigate(configData.routes.homeBanners.homeBanners);
    } catch (error) {
      if (error?.inner)
        error.inner.forEach((error) => showSnackbar(error.message, "error"));
      showSnackbar(error.message, "error");
      setLoading(false);
    }
  };

  const onChange = (e: any) => {
    const { name, value } = e?.target;
    let data = {
      ...bannerDetails,
      [name]: ["isActive", "isTitleBold", "isBodyBold"].includes(name)
        ? !bannerDetails[name]
        : value,
    };
    if (["title", "body"].includes(name)) {
      data[`${name}Trans`]["en"] = value;
    }
    setBannerDetails((state) => ({ ...state, ...data }));
  };

  const upload = async (url) => {
    try {
      const defaultDims = DEFAULT_IMAGE_DIMENSIONS[ImageType.BANNER];
      const resp = await uploadImageV2({
        data: image,
        uploadUrl: url,
        resize: true,
        resizeToWidth: imageDimensions?.targetWidth || defaultDims.width,
        resizeToHeight: imageDimensions?.targetHeight || defaultDims.height,
      });

      if (resp.status === 200)
        showSnackbar(resp.message ?? "Image Uploaded", "success");
      return resp;
    } catch (error) {
      showSnackbar(error.message || "Error uploading image!");
    }
  };

  const onSelectImage = (image: any, dimensions?: any) => {
    setImage(image);
    if (dimensions) {
      setImageDimensions(dimensions);
    }
    setBannerDetails((state) => ({
      ...state,
      fileType: "image/webp",
      imageRepositoryId: null,
    }));
  };

  const onSelectLibraryImage = (e: any) => {
    const { value } = e?.target;
    setImage(null);
    setBannerDetails((state) => ({
      ...state,
      imageRepositoryId: value || null,
      fileType: "",
    }));
  };

  const fetchBannerInfo = async () => {
    try {
      setLoading(true);
      const resp = await getBannerInfo(id);
      if (resp?.data) {
        let titleTrans = { ...resp.data?.titleTrans };
        let bodyTrans = { ...resp.data?.bodyTrans };

        if (resp?.data?.body && !bodyTrans["en"]) {
          bodyTrans["en"] = resp.data?.body;
        } else resp.data.body = bodyTrans["en"];
        if (resp?.data?.title && !titleTrans["en"]) {
          titleTrans["en"] = resp.data?.title;
        } else resp.data.title = titleTrans["en"];

        initialSchedule.current = {
          startDate: resp.data?.startDate ?? null,
          endDate: resp.data?.endDate ?? null,
        };
        setBannerDetails({ ...resp.data, bodyTrans, titleTrans });
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showSnackbar(error.message, "error");
    }
  };

  const getTranslatedData = (field: any) => {
    const _translatedData = Object.entries(bannerDetails?.[field]).map(
      ([key, value]) => ({
        lang: key,
        inputValue: value,
      }),
    );
    if (!_translatedData) return [];
    return _translatedData;
  };

  const handleOpenModal = (type, field) => {
    setModalData((state) => {
      if (type === "view") {
        let data = getTranslatedData(field);
        return { ...state, viewData: data, field, modal: type };
      } else {
        return {
          ...state,
          field,
          modal: type,
          language: "",
          inputValue: "",
        };
      }
    });
    open();
  };

  const handleChange = (event: any) => {
    const { name, value } = event.target;
    setModalData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLangEditClick = (data: any) => {
    close();
    setModalData((state) => ({
      ...state,
      language: data?.lang,
      inputValue: data?.inputValue,
      modal: "add",
    }));
    open();
  };

  const handleLangDeleteClick = (data: any) => {
    if (data.lang === "en") {
      showSnackbar(
        "Cannot delete English translation, as it's a default languange",
        "error",
      );
      return;
    }

    // remove from view data
    setModalData((state) => ({
      ...state,
      viewData: state.viewData.filter((item) => item.lang !== data.lang),
    }));

    setBannerDetails((prev) => {
      prev[modalData.field] = Object.keys(prev[modalData.field]).reduce(
        (acc, key) => {
          if (key !== data.lang) {
            acc[key] = prev[modalData.field][key];
          }
          return acc;
        },
        {},
      );
      return { ...prev };
    });
  };

  const handleAddTranslation = () => {
    const { field } = modalData;
    if (!field) {
      showSnackbar("Confused which field to add the translation", "error");
      return;
    }

    setBannerDetails((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        [modalData.language]: modalData.inputValue,
      },
    }));

    // if english translation is available set it field
    if (modalData.language === "en" && modalData.inputValue !== "") {
      setBannerDetails((details) => ({
        ...details,
        [field?.split("Trans")[0]]: modalData.inputValue,
        [field]: { ...details[field], en: modalData.inputValue },
      }));
    }
    setModalData((prev) => ({
      ...prev,
      inputValue: "",
      language: "",
      field: null,
    }));
    close();
  };

  const handleOperatorChange = (e: any) => {
    const { value } = e.target;
    // Handle SearchableInput format (array of objects with id/label)
    if (Array.isArray(value)) {
      const operatorIds = value.map((item) =>
        typeof item === "object" ? item.id : item,
      );
      setBannerDetails((prev) => ({
        ...prev,
        operators: operatorIds,
      }));
    } else {
      // Handle other input formats
      setBannerDetails((prev) => ({
        ...prev,
        operators: value ? [value] : [],
      }));
    }
  };

  useEffect(
    () => () => {
      if (uploadPreviewUrl) URL.revokeObjectURL(uploadPreviewUrl);
    },
    [uploadPreviewUrl],
  );

  useEffect(() => {
    if (id !== "create") fetchBannerInfo();
    listPackLibraryImages()
      .then((resp) =>
        setLibraryImages(
          (resp?.data ?? []).map((img: any) => ({
            ...img,
            id: img?._id,
            label: img?.imageId,
          })),
        ),
      )
      .catch((error) => showSnackbar(error.message, "error"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fieldGrid = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: 2.5,
    alignItems: "start",
  };

  return (
    <ContentShell
      showBackButton
      onBackButtonClick={cancel}
      title={`${id === "create" ? "Create" : "Edit"} Home Banner`}
      loading={loading}
      actions={
        <Box className={styles.buttonContainer}>
          <Button variant="outlined" color="inherit" onClick={cancel}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" onClick={submit}>
            Save Banner
          </Button>
        </Box>
      }
    >
      <Box sx={{ maxWidth: 980, mx: "auto", pb: 4 }}>
        <SectionCard
          step={1}
          icon={<ArticleOutlinedIcon fontSize="small" />}
          title="Banner Content"
          subtitle="What the banner says and where it leads"
          accent="#1565c0"
          isComplete={
            !!bannerDetails.title &&
            !!bannerDetails.body &&
            !!bannerDetails.order
          }
        >
          <Box sx={fieldGrid}>
            <SimpleTextInput
              label="Title"
              name="title"
              value={bannerDetails.title}
              onChange={onChange}
              button={
                <TranslationButtons
                  field={"titleTrans"}
                  onClick={handleOpenModal}
                />
              }
            />
            <SimpleTextInput
              label="Body"
              name="body"
              value={bannerDetails.body}
              onChange={onChange}
              button={
                <TranslationButtons
                  field={"bodyTrans"}
                  onClick={handleOpenModal}
                />
              }
            />
            <SimpleSelectInput
              label="Navigation Path"
              name="navigationPath"
              value={bannerDetails.navigationPath}
              options={[{ id: "STORE", label: "Store" }]}
              onChange={onChange}
            />
            <SimpleTextInput
              label="Banner Order"
              name="order"
              type="number"
              value={bannerDetails.order}
              onChange={onChange}
            />
            <ColorPicker
              label={"Text Color"}
              name="textColor"
              value={bannerDetails.textColor}
              onChange={onChange}
              customStyle={{ width: "100%", marginTop: 20 }}
            />
            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
              <SimpleSwitch
                legendText={"Title Style"}
                name="isTitleBold"
                value={bannerDetails.isTitleBold}
                label={bannerDetails.isTitleBold ? "Bold" : "Normal"}
                onChange={onChange}
              />
              <SimpleSwitch
                legendText={"Body Style"}
                name="isBodyBold"
                value={bannerDetails.isBodyBold}
                label={bannerDetails.isBodyBold ? "Bold" : "Normal"}
                onChange={onChange}
              />
            </Box>
          </Box>
        </SectionCard>

        <SectionCard
          step={2}
          icon={<ImageOutlinedIcon fontSize="small" />}
          title="Banner Image"
          subtitle="Pick an image from the Image Repository, or upload your own"
          accent="#00838f"
          isComplete={Boolean(previewImageUrl)}
        >
          <Box
            sx={{
              display: "flex",
              gap: 3,
              flexWrap: "wrap",
              alignItems: "flex-start",
            }}
          >
            <Box
              sx={{
                flex: 1,
                minWidth: 260,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Choose one image source. A library image is shared, so it can
                back several banners and packs.
              </Typography>
              <SimpleSelectInput
                label="Library Image"
                name="imageRepositoryId"
                value={selectedLibraryImage ? bannerDetails.imageRepositoryId : ""}
                options={libraryImages}
                onChange={onSelectLibraryImage}
              />
              <FilePicker
                label="Upload Custom Image"
                value={image}
                name="bannerImage"
                showPreview={false}
                onFileSelect={onSelectImage}
                imageType={ImageType.BANNER}
                enableDimensionControl={true}
                metaData={{
                  note: `Recommended: ${
                    DEFAULT_IMAGE_DIMENSIONS[ImageType.BANNER].width
                  } x ${
                    DEFAULT_IMAGE_DIMENSIONS[ImageType.BANNER].height
                  } pixels. Images will be resized and converted to WebP.`,
                }}
              />
            </Box>
            <BannerPreview
              imageUrl={previewImageUrl}
              title={bannerDetails.title}
              body={bannerDetails.body}
              textColor={bannerDetails.textColor}
              isTitleBold={bannerDetails.isTitleBold}
              isBodyBold={bannerDetails.isBodyBold}
            />
          </Box>
        </SectionCard>

        <SectionCard
          step={3}
          icon={<PublicOutlinedIcon fontSize="small" />}
          title="Audience"
          subtitle="Which territory and operators see this banner"
          accent="#2e7d32"
        >
          <Box sx={fieldGrid}>
            <SimpleSelectInput
              label="Territory"
              name="territory"
              value={bannerDetails?.territory || ""}
              options={sessionHelperInstance.countryOptions}
              onChange={onChange}
            />
            <SearchableInput
              selected={(bannerDetails.operators || []).map((operatorId) => {
                const operator = sessionHelperInstance.operatorOptions?.find(
                  (op) => op.id === operatorId,
                );
                return {
                  id: operatorId,
                  label: operator?.label || operatorId,
                };
              })}
              isMultiSelect={true}
              enableSelectAll={true}
              label={"Operators (Optional)"}
              name={"operators"}
              options={sessionHelperInstance.operatorOptions || []}
              onOptionSelect={handleOperatorChange}
              placeholder="Select operators (optional)"
              loading={loading}
            />
          </Box>
        </SectionCard>

        <SectionCard
          step={4}
          icon={<CalendarMonthOutlinedIcon fontSize="small" />}
          title="Status & Schedule"
          subtitle="Manage the status by hand, or set an automatic window in UTC"
          accent="#6a1b9a"
        >
          <Box sx={{ mb: 3 }}>
            <SimpleSwitch
              legendText={"Banner Status"}
              name="isActive"
              value={bannerDetails.isActive}
              label={bannerDetails.isActive ? "Active" : "Inactive"}
              onChange={onChange}
              disabled={isStatusScheduleControlled}
              helperText={
                isStatusScheduleControlled
                  ? "Set by the schedule. Clear the dates to control the status here."
                  : undefined
              }
            />
          </Box>
          <ScheduleFields
            itemLabel="banner"
            startDate={bannerDetails.startDate}
            endDate={bannerDetails.endDate}
            savedStartDate={initialSchedule.current.startDate}
            savedEndDate={initialSchedule.current.endDate}
            disabled={isScheduleBlockedByStatus}
            onChange={(field, value) =>
              setBannerDetails((state) => ({ ...state, [field]: value }))
            }
          />
        </SectionCard>

        <SectionCard
          step={5}
          icon={<TranslateIcon fontSize="small" />}
          title="Translations"
          subtitle="Upload title and body translations in bulk"
          accent="#5d4037"
        >
          <BulkTranslationUpload
            assetType="BANNER"
            assetId={id !== "create" ? id : undefined}
            fields={[
              { label: "Title", fieldName: "titleTrans" },
              { label: "Body", fieldName: "bodyTrans" },
            ]}
            {...(id === "create"
              ? { deferUpload: true, onFileChange: setTranslationFile }
              : { onSuccess: fetchBannerInfo })}
          />
        </SectionCard>
      </Box>
      <ViewLanguageModal
        isMultiline={true}
        label="Select Language"
        isOpen={isOpen}
        close={close}
        onChange={handleChange}
        handleOnSubmit={handleAddTranslation}
        actionHandlers={{
          handleLangEditClick: handleLangEditClick,
          handleLangDeleteClick: handleLangDeleteClick,
        }}
        modalData={modalData}
        tableColumns={[
          {
            id: "lang",
            label: "Language",
            alignHead: "center",
            alignBody: "center",
          },
          {
            id: "inputValue",
            label: "Translation",
            alignBody: "center",
            alignHead: "center",
          },
          {
            id: "actions",
            label: "Actions",
            alignHead: "center",
            alignBody: "center",
          },
        ]}
      />
    </ContentShell>
  );
};

export default HomeBannerEditor;
